import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import type { CreateSessionResponse } from "@fiket/booth-schema";
import { GUEST_RETENTION_DAYS } from "@fiket/booth-schema";
import { Printer, RotateCcw, TriangleAlert } from "lucide-react";
import { useRouter } from "../../store/router";
import { useBoothConfig } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { useOutputAspect } from "../../hooks/useStyles";
import { useVideoLoading } from "../../hooks/useVideoLoading";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";
import { QRCode2D } from "./QRCode";
import { shareQrUrl } from "./shareQr";
import { canShareFile, classifyShareError, dataUrlToFile } from "./sharePrint";
import { createPrintJob } from "../Print/printJobApi";
import { SessionApiError, createSession } from "./sessionApi";
import { useSessionVideo } from "./useSessionVideo";
import { deriveVideoCard, printShareBodyKey, videoFrameAspect } from "./videoDelivery";
import { trackError } from "../../telemetry/activity";

/** 받기 화면 무동작 → thanks (ms) */
const IDLE_TO_THANKS_MS = 120_000;

type UploadState =
  | { kind: "idle" }
  | { kind: "creating" }
  | { kind: "uploading"; progress: number }
  | { kind: "confirming" }
  | { kind: "done"; shortCode: string; downloadUrl: string }
  | { kind: "error"; message: string };

/**
 * STAGE · PrintShare(사진 받기) — 세션 발급(+videoJobId) → S3 presigned PUT → 확정 → QR.
 * 왼쪽 인화지, 오른쪽 QR 박스와 진행 상태. 영상을 시작했으면 왼쪽이 **사진 카드 + 영상 카드 두 장**(`.deliver--video`) —
 * 인화는 사진만 하지만 손님이 받는 결과물이 둘이라 둘 다 보여준다. 영상 카드 비율은 서버 후처리 결과 `videoAspect`(BOOTH-85: 자막 띠 있으면 2:3,
 * 없으면 3:4) 를 따르고(`videoFrameAspect`), 값이 없으면(구 레코드·후처리 실패) 사진과 같은 3:4 프레임으로 Veo 9:16 띠를 cover 로 잘라낸다.
 * 지연 전달(BOOTH-60): 키오스크는 Veo 완료를 기다리지 않는다 — 세션 본문의 `videoJobId` 로 서버가 job 을 세션에 붙이고,
 * 이 화면은 `GET /api/sessions/:code/video` 를 5초마다 폴링해 영상 카드를 "만드는 중"(쉬머) → 영상으로 바꾼다. 손님이 먼저 떠나도
 * 서버 스케줄러가 완료를 세션에 붙여 QR 공유 페이지에 나타난다. 실패면 사진만 + 안내(영상분 결제됐으면 환불 문구).
 * 결제 유효 ON 부스는 세션 본문에 paymentId 를 실어 서버가 결제 ↔ 세션을 상호 연결한다(402 PAYMENT_REQUIRED 면 결제 화면으로, 결제 뒤엔 다시 찍기 없음) (BOOTH-61/74).
 */
export function PrintShareView() {
  const go = useRouter((s) => s.go);
  const setShortCode = useRouter((s) => s.setShortCode);
  // 변환 결과가 우선. 변환 실패 후 "그대로 진행"한 경우엔 원본 사진을 공유한다(review 와 동일한 표시).
  const transformedDataUrl = useRouter(
    (s) => s.capturedShots[0]?.transformedDataUrl ?? s.capturedShots[0]?.dataUrl ?? null
  );
  const videoJobId = useRouter((s) => s.videoJobId);
  const videoStatus = useRouter((s) => s.videoStatus);
  // 생성된 영상 URL — 있으면 사진 카드 옆에 영상 카드를 나란히 보여준다(사용자 요청 2026-10-13 "영상이 나오는 UI").
  const videoUrl = useRouter((s) => s.videoUrl);
  const setVideoReady = useRouter((s) => s.setVideoReady);
  const setVideoFailed = useRouter((s) => s.setVideoFailed);
  // 영상 항목이 실제로 결제됐을 때만 실패 안내에 환불 문구 (BOOTH-80)
  const videoCharged = useRouter((s) => s.paymentVideoCharged);
  // 세션을 어느 부스 소속으로 기록할지 — 마운트 시 로드된 부스 설정에서.
  const boothId = useBoothConfig((s) => s.config?.booth.id ?? null);
  // 프린터 없는 부스(콘솔 부스 관리 "인쇄" 끔) — 인쇄하기 대신 "완료" 로 thanks 로 보낸다. 없으면 true.
  const printEnabled = useBoothConfig((s) => s.config?.booth.printEnabled ?? true);
  // 인쇄 경로 (BOOTH-59). "share" 면 window.print 대신 결과 JPEG 를 공유 시트로 넘긴다 — iPad AirPrint 문서 인쇄의
  // 흰 테두리 우회 실험. 공유 시트를 못 쓰는 환경이면 dialog(print 스테이지)로 폴백.
  const printMode = useBoothConfig((s) => s.config?.booth.printMode ?? "dialog");
  // 에이전트 최근 30초 하트비트 여부 — 어트랙트 복귀 시 재조회된 스냅샷. 경고만 하고 버튼은 막지 않는다(큐에 남기면 자동 인쇄).
  const printAgentOnline = useBoothConfig((s) => s.config?.printAgentOnline ?? false);
  const setPrintId = useRouter((s) => s.setPrintId);
  const { t, language } = useI18n();
  const aspect = useOutputAspect();
  const clearShots = useRouter((s) => s.clearShots);
  const clearVideo = useRouter((s) => s.clearVideo);
  const resetSession = useRouter((s) => s.resetSession);
  const paymentId = useRouter((s) => s.paymentId);
  const beginPayment = useRouter((s) => s.beginPayment);
  const clearPayment = useRouter((s) => s.clearPayment);
  const refresh = useBoothConfig((s) => s.refresh);
  // 통계 축 (BOOTH-87) — 세션 레코드에 손님이 고른 테마·스타일·언어·방문 id 를 남긴다
  const selectedThemeId = useRouter((s) => s.selectedThemeId);
  const selectedStyleId = useRouter((s) => s.selectedStyleId);
  const visitId = useRouter((s) => s.visitId);
  // 개인정보 동의 (BOOTH-66) — consentId 우선, POST 실패 시 폴백으로 consent 인라인 전달
  const consentId = useRouter((s) => s.consentId);
  const consentPending = useRouter((s) => s.consentPending);
  // 완성 영상 mp4 첫 프레임 전 쉬머 (BOOTH-93) — 생성 대기 쉬머(pending 카드)가 끝난 뒤 실제 파일 로드 구간
  const { loading: videoLoading, bind: videoBind } = useVideoLoading();

  // 사용자가 QR 을 찍고 떠나면 화면이 남는다 — 120초 무동작이면 thanks 로 (터치마다 리셋)
  useEffect(() => {
    let id = window.setTimeout(() => go("thanks"), IDLE_TO_THANKS_MS);
    const bump = () => {
      window.clearTimeout(id);
      id = window.setTimeout(() => go("thanks"), IDLE_TO_THANKS_MS);
    };
    window.addEventListener("pointerdown", bump, { passive: true });
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("pointerdown", bump);
    };
  }, [go]);
  const [upload, setUpload] = useState<UploadState>({ kind: "idle" });
  /** agent 모드 인쇄 요청 진행. 실패해도 QR 화면에 남아 다시 누를 수 있다(서버가 같은 세션의 진행 중 작업을 재사용) */
  const [printReq, setPrintReq] = useState<"idle" | "creating" | "error">("idle");
  const printAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => printAbortRef.current?.abort(), []);
  const attemptRef = useRef(0);
  /**
   * 같은 사진의 재시도 사이에 보존되는 진행 상태.
   * S3 PUT 은 성공했는데 /uploaded 확정만 실패하면, 재시도가 세션을 새로 만들고 다시
   * PUT 하면서 서버엔 imageUrl 없는 고아 사진(갤러리·동기화에 영영 안 뜸)이 남는다.
   * 그래서 재시도는 세션 생성·업로드를 건너뛰고 확정만 다시 호출한다 (BOOTH-3).
   */
  const flowRef = useRef<{
    forDataUrl: string | null;
    session: CreateSessionResponse | null;
    sessionPromise: Promise<CreateSessionResponse> | null;
    uploaded: boolean;
  }>({ forDataUrl: null, session: null, sessionPromise: null, uploaded: false });

  const runUpload = useCallback(async () => {
    if (!transformedDataUrl) {
      setUpload({ kind: "error", message: "변환된 사진을 찾을 수 없어요" });
      return;
    }
    if (!boothId) {
      setUpload({ kind: "error", message: "부스 정보를 불러오지 못했어요" });
      return;
    }
    const attempt = ++attemptRef.current;
    // 다른 사진이면 진행 상태 초기화
    if (flowRef.current.forDataUrl !== transformedDataUrl) {
      flowRef.current = { forDataUrl: transformedDataUrl, session: null, sessionPromise: null, uploaded: false };
    }
    const flow = flowRef.current;
    try {
      let session = flow.session;
      if (!session) {
        setUpload({ kind: "creating" });
        // 같은 사진에 대한 세션 생성은 1건만 — StrictMode 이중 effect·재시도 재진입이 같은 요청을 공유한다.
        // 결제가 묶인 세션이 둘이면 두 번째는 409 PAYMENT_ALREADY_USED 라 손님이 돈을 내고도 QR 을 못 받는다 (BOOTH-74 스모크 발견).
        // 영상 job 이 있으면(제작 중·완료) 세션에 붙여 달라고 함께 보낸다 — 서버가 완료 시 자동 연결 (BOOTH-60 지연 전달)
        const attachJobId = videoJobId && (videoStatus === "generating" || videoStatus === "ready") ? videoJobId : null;
        flow.sessionPromise ??= createSession(boothId, paymentId, attachJobId, {
          themeId: selectedThemeId ?? undefined,
          styleId: selectedStyleId ?? undefined,
          language,
          visitId: visitId ?? undefined,
        }, { consentId, consent: consentPending }).then(
          (s) => { flow.session = s; return s; },
          (err) => { flow.sessionPromise = null; throw err; }
        );
        session = await flow.sessionPromise;
        if (attempt !== attemptRef.current) return;
      }

      if (!flow.uploaded) {
        const blob = await dataUrlToBlob(transformedDataUrl);
        setUpload({ kind: "uploading", progress: 0 });
        await uploadWithProgress(session.uploadUrl, blob, (p) => {
          if (attempt !== attemptRef.current) return;
          setUpload({ kind: "uploading", progress: p });
        });
        if (attempt !== attemptRef.current) return;
        flow.uploaded = true;
      }

      setUpload({ kind: "confirming" });
      await confirmUploadedWithRetry(session.shortCode, () => attempt !== attemptRef.current);
      if (attempt !== attemptRef.current) return;

      setShortCode(session.shortCode, session.downloadUrl);
      setUpload({ kind: "done", shortCode: session.shortCode, downloadUrl: session.downloadUrl });
    } catch (err) {
      if (attempt !== attemptRef.current) return;
      // F5: 서버는 사진 결제가 걸려 있는데 키오스크는 모르고 왔다(config 낡음) → 재조회 후 사진 결제 화면으로.
      // 영상을 이미 만들었어도 withVideo=false — true 로 보내면 paid 뒤 videoGenerate 로 되돌아가 완성된 영상을 다시 만든다(선재 버그, BOOTH-80).
      if (err instanceof SessionApiError && err.code === "PAYMENT_REQUIRED") {
        void refresh();
        clearPayment();
        beginPayment(false);
        go("payment");
        return;
      }
      console.error("[PrintShare] 업로드 실패", err);
      if (err instanceof SessionApiError) {
        if (err.httpStatus >= 400 && err.httpStatus < 500) {
          trackError("session_rejected", err, { message: `${err.code}: ${err.message}` });
        } else {
          trackError("session_create_failed", err);
        }
      } else {
        trackError("upload_failed", err);
      }
      setUpload({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }, [transformedDataUrl, boothId, setShortCode, videoJobId, videoStatus, paymentId, refresh, clearPayment, beginPayment, go]);

  useEffect(() => {
    void runUpload();
    return () => {
      attemptRef.current++; // 스테이지 이탈 시 진행 중 플로우 무효화
    };
  }, [runUpload]);

  const done = upload.kind === "done";
  const failed = upload.kind === "error";

  // 영상 상태 폴링 (BOOTH-60 지연 전달) — 세션이 생긴 뒤, 영상을 시작한 세션에서만. 서버 값이 오면 store 에 반영한다.
  const { remote: remoteVideo } = useSessionVideo(done ? upload.shortCode : null, videoStatus !== "idle" && Boolean(videoJobId));
  useEffect(() => {
    if (!remoteVideo) return;
    if (remoteVideo.videoStatus === "ready" && remoteVideo.videoUrl) setVideoReady(remoteVideo.videoUrl);
    else if (remoteVideo.videoStatus === "failed" || remoteVideo.videoStatus === "none") setVideoFailed();
  }, [remoteVideo, setVideoReady, setVideoFailed]);
  const videoCard = deriveVideoCard(videoStatus, remoteVideo);
  const showMedia = videoCard === "pending" || (videoCard === "ready" && Boolean(videoUrl));

  // 공유 시트 인쇄용 File 은 업로드 완료 시 미리 만든다 — navigator.share 는 사용자 제스처(transient activation) 안에서
  // 불려야 하므로 클릭 핸들러에서 dataURL→Blob 을 기다리면 Safari 가 NotAllowedError 를 낼 수 있다.
  const [shareFile, setShareFile] = useState<File | null>(null);
  useEffect(() => {
    if (printMode !== "share" || !done || !transformedDataUrl) {
      setShareFile(null);
      return;
    }
    let cancelled = false;
    dataUrlToFile(transformedDataUrl, upload.shortCode)
      .then((f) => { if (!cancelled) setShareFile(f); })
      .catch((err) => {
        console.warn("[PrintShare] 공유 파일 생성 실패 — 인쇄 다이얼로그로 폴백", err);
        trackError("share_print_failed", err);
      });
    return () => { cancelled = true; };
    // upload.shortCode 는 done 일 때만 존재하고 done 이 바뀔 때 함께 바뀐다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printMode, done, transformedDataUrl]);
  const shareReady = printMode === "share" && shareFile !== null && canShareFile(navigator, shareFile);

  /** 인쇄하기 — agent 모드면 서버 큐, share 모드면 공유 시트(→ 운영자가 "프린트"), 아니면 print 스테이지(window.print). */
  const onPrint = () => {
    // agent 모드(BOOTH-39): 서버 큐에 작업을 넣고 print 스테이지가 폴링한다. window.print·공유 시트 없음.
    if (printMode === "agent") {
      if (upload.kind !== "done" || printReq === "creating") return;
      const controller = new AbortController();
      printAbortRef.current = controller;
      setPrintReq("creating");
      createPrintJob(upload.shortCode, controller.signal)
        .then((job) => {
          setPrintId(job.printId);
          go("print");
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.warn("[PrintShare] 인쇄 요청 실패", err);
          trackError("print_request_failed", err);
          setPrintReq("error");
        });
      return;
    }
    if (!shareReady || !shareFile) {
      go("print");
      return;
    }
    // await 없이 즉시 호출 — 제스처 활성 상태를 잃지 않게.
    navigator
      .share({ files: [shareFile] })
      .then(() => go("thanks"))
      .catch((err) => {
        const outcome = classifyShareError(err);
        if (outcome === "cancelled") return; // 시트를 닫음 — 받기 화면에 그대로
        console.warn("[PrintShare] 공유 시트 인쇄 실패 — 인쇄 다이얼로그로 폴백", err);
        trackError("share_print_failed", err);
        go("print");
      });
  };
  const progress = uploadProgressFor(upload);
  // QR 은 손님이 고른 언어로 공유 페이지를 열도록 ?lang= 을 싣는다 (BOOTH-32) — 인쇄·thanks 화면도 같은 주소 (BOOTH-63)
  const downloadUrl = shareQrUrl(done ? upload.downloadUrl : null, language);
  const progressLabel = t.printShare.uploading;

  const retake = () => {
    clearShots();
    clearVideo();
    go("preview");
  };
  const arStyle = { "--ar": String(aspect.w / aspect.h), "--ar-css": `${aspect.w} / ${aspect.h}` } as CSSProperties;
  // 영상 카드만 — 후처리 비율(2:3 | 3:4). 제작 중 자리표시는 아직 비율을 모르니 사진 비율 그대로
  const videoArStyle = { "--ar-css": videoFrameAspect(remoteVideo) } as CSSProperties;

  return (
    <div className="view view--deliver view--stackable">

      {/* 좌 흰 사진 카드(영상 있으면 사진·영상 두 장) / 우 QR 박스 + 안내. 폰에서는 QR 이 먼저(order) */}
      <div className={`view__body deliver${showMedia ? " deliver--video" : ""}`}>
        {showMedia ? (
          <div className="deliver__media">
            <div className="media-item">
              <figure className="photo-card">
                {transformedDataUrl ? <img src={transformedDataUrl} alt="" draggable={false} style={arStyle} /> : null}
              </figure>
              <span className="media-item__label">{t.printShare.photoLabel}</span>
            </div>
            <div className="media-item">
              {videoCard === "ready" && videoUrl ? (
                <figure className="photo-card photo-card--video" style={videoArStyle} aria-busy={videoLoading}>
                  <div className="photo-card__media">
                    <video key={videoUrl} {...videoBind} src={videoUrl} autoPlay loop muted playsInline />
                    {videoLoading ? <span className="shimmer" aria-hidden /> : null}
                  </div>
                </figure>
              ) : (
                <figure className="photo-card photo-card--video photo-card--pending" role="status" aria-live="polite">
                  <div className="pending-fill" style={arStyle}>
                    <span className="kicker">MOVING PORTRAIT</span>
                    <span className="pending-fill__label">{t.printShare.videoPendingLabel}</span>
                  </div>
                </figure>
              )}
              <span className="media-item__label">{t.printShare.videoLabel}</span>
            </div>
          </div>
        ) : (
          <figure className="photo-card">
            {transformedDataUrl ? <img src={transformedDataUrl} alt="" draggable={false} style={arStyle} /> : null}
          </figure>
        )}
        <div className="deliver__side">
          <div className="qr-box" role="img" aria-label={t.printShare.qrAlt}>
            {downloadUrl ? (
              <QRCode2D value={downloadUrl} size={432} />
            ) : (
              <div className="qr-skeleton" style={failed ? { color: "var(--color-error)" } : undefined}>
                {failed ? t.printShare.uploadError : (
                  <div style={{ width: "80%" }}>
                    <div className="note" style={{ marginBottom: 8 }}>{progressLabel} · {Math.round(progress * 100)}%</div>
                    <div className="bar"><span style={{ width: `${Math.round(progress * 100)}%` }} /></div>
                  </div>
                )}
              </div>
            )}
          </div>
          <span className="kicker">{t.printShare.kicker}</span>
          <h2>{t.printShare.qrHeading}</h2>
          <p>
            <Lines text={`${t.printShare[printShareBodyKey(videoCard)]}\n${t.printShare.desc2(GUEST_RETENTION_DAYS)}`} />
          </p>
          <p className="privacy">
            <TriangleAlert size={20} aria-hidden />
            <span><Lines text={t.printShare.privacy(GUEST_RETENTION_DAYS).replace(" · ", "\n")} /></span>
          </p>
        </div>
      </div>

      <div className="view__cta actions">
        {paymentId ? null : (
          <Button variant="secondary" onClick={retake}><RotateCcw size={22} aria-hidden />{t.printShare.retake}</Button>
        )}
        {failed ? (
          <Button onClick={() => void runUpload()}>{t.printShare.retry}</Button>
        ) : printEnabled ? (
          <Button disabled={!done || printReq === "creating"} onClick={onPrint}><Printer size={24} aria-hidden />{t.printShare.printCta}</Button>
        ) : (
          <Button disabled={!done} onClick={() => go("thanks")}>{t.printShare.doneCta}</Button>
        )}
      </div>
      {shareReady ? (
        <div className="view__sub"><p className="note">{t.printShare.shareHint}</p></div>
      ) : null}
      {printMode === "agent" && printEnabled && done && printReq !== "error" && !printAgentOnline ? (
        <div className="view__sub"><p className="note">{t.printShare.agentOffline}</p></div>
      ) : null}
      {printReq === "error" ? (
        <div className="view__sub"><p className="note" style={{ color: "var(--color-error)" }}><Lines text={t.print.failed} /></p></div>
      ) : null}
      {videoCard === "failed" && videoCharged ? (
        <div className="view__sub"><p className="note">{t.videoGenerate.refunded}</p></div>
      ) : null}
    </div>
  );
}

function uploadProgressFor(state: UploadState): number {
  switch (state.kind) {
    case "idle":
    case "creating":
      return 0.05;
    case "uploading":
      return 0.05 + state.progress * 0.85;
    case "confirming":
      return 0.95;
    case "done":
      return 1;
    case "error":
      return 0;
  }
}


async function confirmUploaded(shortCode: string): Promise<void> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(shortCode)}/uploaded`, { method: "POST" });
  if (!res.ok) {
    // 4xx 는 재시도해도 같으므로 즉시 실패, 5xx·네트워크만 재시도 대상.
    const err = new Error(`confirm ${res.status}`);
    (err as Error & { retryable?: boolean }).retryable = res.status >= 500;
    throw err;
  }
}

const CONFIRM_RETRY_DELAYS_MS = [500, 1500, 3000];

/**
 * 업로드 확정 재시도 — 서버는 멱등이라 중복 호출 안전.
 * 순단으로 확정만 놓치면 S3 엔 사진이 있는데 세션은 created 로 남는 고아가 생기므로
 * 짧은 백오프로 3회 더 시도한다. `cancelled()` 가 true 면(스테이지 이탈) 중단.
 */
async function confirmUploadedWithRetry(
  shortCode: string,
  cancelled: () => boolean
): Promise<void> {
  let lastErr: unknown;
  for (let i = 0; i <= CONFIRM_RETRY_DELAYS_MS.length; i++) {
    try {
      await confirmUploaded(shortCode);
      return;
    } catch (err) {
      lastErr = err;
      const retryable =
        (err as Error & { retryable?: boolean }).retryable ?? true; // 네트워크 예외는 재시도
      if (!retryable || i === CONFIRM_RETRY_DELAYS_MS.length) break;
      console.warn(`[PrintShare] confirm 실패, ${CONFIRM_RETRY_DELAYS_MS[i]}ms 후 재시도`, err);
      await new Promise((r) => setTimeout(r, CONFIRM_RETRY_DELAYS_MS[i]));
      if (cancelled()) return;
    }
  }
  trackError("upload_confirm_failed", lastErr);
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

/** fetch 는 PUT 진행률을 노출하지 않음 → XMLHttpRequest 로 우회. */
function uploadWithProgress(url: string, blob: Blob, onProgress: (ratio: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", blob.type || "image/jpeg");
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress(ev.loaded / ev.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new Error(`upload ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("upload network error"));
    xhr.send(blob);
  });
}
